"use client";

// Live "memory organism" — the evolved BloomField engine (apps/web/lib/organism)
// mounted as a self-contained canvas. The engine is imported dynamically inside
// useEffect because postprocess.ts touches `document` at import time (SSR-unsafe).
//
// The parent positions/sizes/blurs this; the organism fills its wrapper. It:
//   • keeps its ground synced to the theme so it blends + reacts (glow on dark),
//   • publishes the live palette to --organism-hue/color/core/fringe, so the
//     whole UI retints with it (the design system reads these everywhere),
//   • when `wander` is set (signed-out surfaces), slowly journeys through a
//     curated hue arc so it radiates shifting colors and the UI follows,
//   • focuses/zooms to a region + rotates when `focus`/`rotation` change.

import { useEffect, useRef } from "react";
import { useTheme } from "./ThemeProvider";
import { harmonize, derivePalette, rgbToCss, type ColorState, type RGB } from "../lib/organism/color";
import { computeThemeVars } from "../lib/theme";
import type { MacroKey, MacroState } from "../lib/organism/macro-tuners";
import { useLiveMemory } from "../lib/organism/useLiveMemory";
import { CALM_EVENT, currentOrganismCalm } from "../lib/organism/calmBus";
import { FOCUS_EVENT, currentNettiFocus } from "../lib/os/nettiFocus";
import { isModestGpu, isSoftwareGpu } from "../lib/organism/deviceBudget";
import { isLowPowerDevice, isTouchScreenDevice } from "../lib/organism/deviceBudget";
import { reportClientError } from "../lib/os/reportError";

export type OrganismFocus = {
  angle: number; // radians around the organism
  dist: number; // 0..1 outward
  zoom?: number;
  radius?: number;
};

type Props = {
  /** Focus/zoom/dim onto a region; null clears (whole organism). */
  focus?: OrganismFocus | null;
  /** Global rotation in radians (eased) — spins as it moves between sections. */
  rotation?: number;
  /** Continuous very-slow AMBIENT spin so the organism always feels alive when
   *  idle. Composes additively with `rotation` and only ever advances forward. */
  spin?: boolean;
  /** Bump this monotonic counter to make the organism REACT to a memory-
   *  affecting action — a forward spin pulse + a brief swell that settles back. */
  pulse?: number;
  /** LIVE MODE — the organism wires its own nervous system (useLiveMemory): it
   *  fires its neurons automatically on any in-app `innernet:memory` event AND on
   *  Supabase realtime INSERTs to the signed-in user's `activity_events` feed.
   *  Composes with the explicit `pulse` prop. Default ON; set false for purely
   *  decorative surfaces (e.g. signed-out marketing) that should not subscribe. */
  live?: boolean;
  /** Ground override (defaults to the theme ground so it blends). */
  bg?: RGB;
  /** Palette override (defaults to the theme organism). */
  color?: ColorState;
  /** Memory age 0..1 (size/spread) — the NATURAL size (see the Law below). */
  age?: number;
  /** Listen to the imperative age bus (window `innernet:organism-age`). A
   *  timeline scrubs the bloom's age through it without re-rendering React; null
   *  in the event hands the bloom back to the `age` prop (its natural size). */
  ageBus?: boolean;
  /** A PRIVATE CHANNEL on the buses. The age/drive buses are window events, so
   *  two mounts on one page (the birth overlay over the landing's own bloom)
   *  would steer each other. A mount with a `busKey` only answers events whose
   *  `detail.bus` matches it; a mount without one ignores any tagged event. */
  busKey?: string;
  /** Listen to the imperative DRIVE bus (window `innernet:organism-drive`,
   *  detail `{ color?, bg?, rotation? }`). A scroll controller can repaint the
   *  bloom's colour / ground / rotation at 60fps without ever re-rendering React
   *  — mirrors the age bus. Off by default, so existing surfaces are untouched. */
  driveBus?: boolean;
  /** Slowly age over time (living growth). */
  grow?: boolean;
  /** Backing-store undersample — pair with CSS blur for cheap softness. */
  renderScale?: number;
  /** The backing store's DPR ceiling. The default is deliberately below 1 (the
   *  frost over a dashboard bloom hides it); a surface where the creature IS
   *  the picture (the birth) asks for the screen's own density, capped here. */
  maxDpr?: number;
  /** Engine macro overrides (bleed, density, shimmer…) — the in-engine
   *  chromatography dials; prefer these over CSS blur for softness. */
  macros?: Partial<MacroState>;
  seed?: number;
  mode?: "focus" | "background";
  /** Publish the live palette to :root (the global UI tint link). */
  publishHue?: boolean;
  /** Signed-out radiance: slowly journey through a curated hue arc so the whole
   *  UI breathes through living colors. Ignored under reduced-motion. */
  wander?: boolean;
  /** Showpiece mode (the waitlist landing): the organism journeys through a
   *  curated sequence of MOODS — each a full personality of palette + shape +
   *  size — cross-fading continuously so it reads as one living thing that keeps
   *  becoming. Composes with `spin`/`grow`; drives the engine directly (color +
   *  macros now, the age TARGET handed to the life loop). Ignored under
   *  reduced-motion (the organism then holds a single calm mood). */
  showcase?: boolean;
  /** LIVE TUNER PATCH for the future engine — a surface dressing THIS netti
   *  (more venation, brighter cells) without minting a second one. Keys are
   *  FutureTuners keys (venation · nodes · innerLight · glass · cellSize · …),
   *  values 0..1. Applied at mount and again whenever the patch changes; drop
   *  it (undefined) and netti walks back to itself via `restoreIdentity`.
   *  law_one_netti: this dresses the ONE organism, it never mints another. */
  tune?: Record<string, number>;
  /** THE BODY THIS PERSON ACTUALLY OWNS, where it differs from the code's mint —
   *  the live mint bands and a founder's hand-tuned override, already resolved
   *  (lib/netti/tuner `resolveBody`). Unlike `tune` this IS identity: it is laid
   *  straight over the seed's mint at mount and again by `restoreIdentity`, so a
   *  section that morphs netti still comes home to the tuned body. */
  body?: Record<string, number>;
  /** WARM COIL variant — render the nautilus/glass-coil organism instead of the
   *  flower. A deliberate mount-level toggle (never derived from a user's traits):
   *  it forces `warm:1` into the engine macros so the `uWarm`-gated shader paths
   *  light up. At `warm=false` (default) the renderer is the exact flower — the
   *  warm macros stay inert. Reuses the SAME minted palette; never re-rolls color.
   *  Only honoured under `engine="coil"` — the future engine has its own body. */
  warm?: boolean;
  /** WHICH GENERATION renders. "future" (default) mounts the next-gen single-pass
   *  SDF organism (lib/organism/future) behind the exact same choreography surface
   *  — focus dives, rotation/spin, firing, age law, publish, veil, pointer. Pass
   *  "coil" to fall back to the previous BloomField/OrganismGL warm-coil engine
   *  (per-mount rollback lever; no call sites need to change). */
  engine?: "future" | "coil";
  /** ASCII VEIL — mount a third overlay canvas that samples the live GL organism
   *  and dissolves it into the ground with a shimmering ASCII glyph field. Off by
   *  default; only mounts a second WebGL2 context when `veil=true`. */
  veil?: boolean;
  /** Per-mount overrides for the veil's shader params (columns, opacity, glow,
   *  threshold, organism…). Merged over asciiConfig()'s defaults, so surfaces that
   *  don't pass it (the dashboard) are byte-identical to before. */
  veilTune?: Record<string, number>;
  /** THE LENS (2026-10-22): this netti answers the page's focus pull
   *  (lib/os/nettiFocus) by drawing itself out of focus in the engine, every
   *  petal, vein and node cell softened where it is drawn, at a fraction of the
   *  pixels (FutureOrganism.setDefocus). OrganismBackdrop passes it wherever
   *  the focus pull is on (`readingFrost`). */
  lens?: boolean;
  /** Imperative handle for tool surfaces (/netti): live-tune the engine + export a
   *  genuinely transparent PNG — all against the SAME canonical component every other
   *  mount uses. Populated on mount, nulled on unmount. */
  apiRef?: React.MutableRefObject<OrganismApi | null>;
  className?: string;
  style?: React.CSSProperties;
};

/** The imperative surface a tool mount can drive (see `apiRef`). */
export type OrganismApi = {
  /** One frame of the actual GL organism as a REAL transparent PNG (data URL).
   *  `px` is the long edge (default 2048) — ask for the size you will use: every
   *  export is a synchronous GPU readback + PNG encode on the main thread, and
   *  its cost is the square of this number. */
  exportPng: (px?: number) => string;
  /** The same frame painted for several grounds (0 paper … 1 glass, see
   *  lib/organism/groundLaw), one shared crop — the registered pair a bake ships. */
  exportPngs: (darks: number[], px?: number) => string[];
  setMacro: (key: MacroKey, v: number) => void;
  setAge: (v: number) => void;
  /** Re-tint from a single hue (analogous harmony, like the auth surfaces). */
  setHue: (hue: number) => void;
  setColor: (c: ColorState) => void;
  setBg: (rgb: RGB) => void;
  setRotation: (rad: number) => void;
  /** Re-mint the arrangement (new seed, same palette). */
  reseed: () => void;
  fire: () => void;
};

/** The ground the page is ACTUALLY painted with — read from the live DOM so
 *  the canvas can never disagree with the body and show as a tonal rectangle
 *  (e.g. when a persisted custom ground sets --bg before React state hydrates). */
function renderedGroundRGB(): RGB | null {
  try {
    // a room painted on the canvas (<html>) leaves <body> clear so the fixed
    // ground wash shows through (useCoTheme) — read whichever one is painted.
    const opaque = (el: Element) => {
      const c = getComputedStyle(el).backgroundColor;
      return /^rgba\(.*,\s*0\)$/.test(c) || c === "transparent" ? null : c;
    };
    const c = opaque(document.body) ?? opaque(document.documentElement) ?? "";
    const m = c.match(/rgba?\(\s*(\d+)[,\s]+(\d+)[,\s]+(\d+)/);
    if (!m) return null;
    return [Number(m[1]) / 255, Number(m[2]) / 255, Number(m[3]) / 255];
  } catch {
    return null;
  }
}

// curated hue journey — legible, on-brand-ish arc: leaf green → teal → sky →
// violet → rose → amber → spring, then loops. The UI's ink stays low-chroma so
// even the far hues read as tasteful tinted neutrals; the accents go vivid.
const HUE_JOURNEY = [108, 152, 196, 250, 306, 344, 24, 64];
const smooth = (t: number) => t * t * (3 - 2 * t);
function lerpHue(a: number, b: number, t: number): number {
  const d = ((b - a + 540) % 360) - 180; // shortest signed arc
  return (((a + d * t) % 360) + 360) % 360;
}
// SHOWCASE — the waitlist landing's living organism. COLOUR drifts continuously
// (one constant-speed hue revolution, no easing humps); SHAPE morphs occasionally.
// The first cut strobed because it changed form/density EVERY tick, so the sim
// re-generated constantly. The engine actually crossfades a form change over
// ~1.1s (it keeps the "leaving" pool and blends), so the fix is cadence: step to
// a NEW form only every SHOWCASE_FORM_MS — comfortably longer than the crossfade —
// and each morph lands cleanly instead of glitching mid-blend. `analogous` keeps
// the colour spread cohesive (a teal→green→blue fan, never a busy triad).
const SHOWCASE_BASE_HUE = 156;
const SHOWCASE_PERIOD_S = 54; // one full, unhurried hue revolution
// silhouettes to morph between (all leaf-like / mid-high "form") and the cadence
const SHOWCASE_FORMS = [0.42, 0.58, 0.5, 0.66, 0.46, 0.62, 0.54, 0.38];
const SHOWCASE_FORM_MS = 7600;

// ─────────────────────────────────────────────────────────────────────────────
// THE LAW OF THE net's NATURAL SIZE
//
// The organism (the "net") always renders at its NATURAL size — a truthful
// readout of how much memory actually exists (ageFromMemory(nodeCount), passed in
// as `age`). Life and time-travel may FLEX that size, but only within a small
// margin and never upward past natural:
//   • breath — a barely-there sine swell, ±BREATH_AMP, so it reads as alive.
//   • a memory write — a brief pulse swell that decays straight back to natural.
//   • scrubbing the timeline — ages DOWN toward the sprout it once was; "today"
//     sits exactly at natural and never balloons beyond it.
// Because size == memory, inflating the present beyond natural would lie about how
// much the user has. Everything here is built to hold that line.
// ─────────────────────────────────────────────────────────────────────────────

export default function MemoryOrganism({
  focus = null,
  rotation = 0,
  spin = false,
  pulse = 0,
  live: liveEnabled = true,
  bg,
  color,
  age = 0.52,
  ageBus = false,
  driveBus = false,
  busKey,
  grow = true,
  renderScale = 0.7,
  maxDpr: maxDprProp,
  macros,
  tune,
  body,
  seed,
  mode = "focus",
  publishHue = true,
  wander = false,
  showcase = false,
  warm = false,
  engine = "future",
  veil = false,
  veilTune,
  lens = false,
  apiRef,
  className,
  style,
}: Props) {
  const theme = useTheme();
  // LIVE FLAGS for the drive interval below. The interval is created once, at
  // mount, and used to close over `publishHue` / `wander` / `showcase` as they
  // were THEN — so a surface whose flags flip after mount (the dashboard once a
  // newborn netti is adopted onto the odyssey: stage showcase off, publish off)
  // kept cycling the hue forever with stale booleans. Refs read the present.
  const publishRef = useRef(publishHue); publishRef.current = publishHue;
  const wanderRef = useRef(wander); wanderRef.current = wander;
  const showcaseRef = useRef(showcase); showcaseRef.current = showcase;
  const colorPropRef = useRef(color); colorPropRef.current = color;
  // the live tuner patch, read by the boot closure (which runs once, async)
  const tuneRef = useRef(tune); tuneRef.current = tune;
  const bodyRef = useRef(body); bodyRef.current = body;
  const lensRef = useRef(lens); lensRef.current = lens;
  // LIVE NERVOUS SYSTEM — the organism subscribes to memory changes itself (the
  // in-app `innernet:memory` bus + Supabase realtime on the user's feed). Each
  // flurry bumps `live.pulse`; we fold it into the same firing path as the
  // explicit `pulse` prop below, so the organism fires automatically on any
  // memory change with no wiring needed at the call sites.
  const liveMem = useLiveMemory({ realtime: liveEnabled });
  const wrapRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const glCanvasRef = useRef<HTMLCanvasElement>(null);
  const overlayCanvasRef = useRef<HTMLCanvasElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fieldRef = useRef<any>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const glRef = useRef<any>(null);
  const live = useRef({ bg, color, theme });
  live.current = { bg, color, theme };
  // rotation authority: `rotBase` is the prop (eased section/dive spin), `rotAmb`
  // is the continuously-accumulating ambient drift — the engine target is their
  // sum, so both only ever advance forward (no spin-back).
  const rotBase = useRef(rotation);
  const rotAmb = useRef(0);
  const pulseAge = useRef(0); // transient age swell from a memory-action pulse
  // AGE AUTHORITY (three refs, one rAF smoother):
  //   • agePropRef  — the NATURAL size from the `age` prop (memory volume).
  //   • busAgeRef   — a timeline's scrub override, or null when not scrubbing.
  //   • displayAge  — the eased value actually rendered; it chases
  //                   (busAgeRef ?? agePropRef) with weight, so any jump (a scrub
  //                   fling, a prop change) is absorbed smoothly — no step jitter.
  // prevDisplayAge feeds the scrub-spin: the bloom turns in proportion to how fast
  // its age is *currently* changing, then settles when you stop scrolling.
  const agePropRef = useRef(age);
  const busAgeRef = useRef<number | null>(null);
  const displayAge = useRef(age);
  const prevDisplayAge = useRef(age);
  const scrubSpinVel = useRef(0);
  // FIRE — the single firing action both the explicit `pulse` prop and the live
  // memory feed call. It (1) fires real neuron STREAMS through the lattice (the
  // visible "neurons firing"), and (2) swells/rotates the body so the whole
  // organism reacts. `burst` (events folded into this flurry) scales how many
  // streams light up and how big the swell is, so a bulk write reads bigger than
  // a single capture. Reduced-motion still fires (memory DID change) but with a
  // calmer swell/spin and a single stream — no excessive motion, no strobe.
  const reduceMotionRef = useRef(false);
  const fire = useRef((burst = 1) => {
    const f = fieldRef.current;
    const reduce = reduceMotionRef.current;
    // (1) fire neuron streams along the lattice — the literal neurons firing.
    if (f?.fireStream) {
      if (reduce) {
        f.fireStream();
      } else {
        // a flurry lights up a few filaments, capped so it never strobes.
        const n = Math.max(1, Math.min(5, Math.round(1 + Math.log2(1 + burst))));
        if (n <= 1) f.fireStream();
        else if (f.fireStreamBurst) f.fireStreamBurst(n);
        else f.fireStream();
      }
    }
    // (2) body reaction — a swell + a forward spin pulse that the life loop
    // settles back. Scaled by burst, gentler under reduced-motion.
    const mag = Math.min(2.0, 0.7 + Math.log2(1 + burst) * 0.5); // 1 event ≈ 1.2×
    pulseAge.current = Math.min(0.34, (reduce ? 0.07 : 0.18) * mag);
    rotAmb.current += (reduce ? 0.12 : 0.55) * mag;
    fieldRef.current?.setRotation?.(rotBase.current + rotAmb.current);
  });

  // mount the engine once (client-only)
  useEffect(() => {
    let disposed = false;
    let ro: ResizeObserver | null = null;
    let driveTimer: ReturnType<typeof setInterval> | null = null;
    let rafId = 0; // the single life-loop rAF (breath + spin + scrub)
    let onPointer: ((e: PointerEvent) => void) | null = null;
    let onPointerLeaveWin: (() => void) | null = null;
    // set when the tint publisher installs its own scroll listener (phones only)
    let publishScrollCleanup: (() => void) | null = null;
    const curAge = age;
    const reduce =
      typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    reduceMotionRef.current = reduce; // the `fire` action reads this for calmer firing
    const doWander = () => !!wanderRef.current && !reduce;
    const doShowcase = () => !!showcaseRef.current && !reduce;
    const doPublish = () => !!publishRef.current;
    // whether the drive was steering colour on the previous tick — when it
    // stops, the colour prop takes back over (one hand-back, not a fade-fight).
    let wasDriving = doWander() || doShowcase();
    let wasPublishing = doPublish();

    const onVis = () => fieldRef.current?.setPaused?.(document.hidden || held);
    // HOLD — one surface (the birth overlay) asks every OTHER bloom on the page
    // to stand still while it plays, so two full organisms never fight for one
    // GPU (a phone under the landing's bloom AND the birth would stutter both).
    // detail: { hold: boolean, except?: busKey } — the excepted mount keeps going.
    let held = false;
    const onHold = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (d.except && d.except === busKey) return;
      held = !!d.hold;
      fieldRef.current?.setPaused?.(document.hidden || held);
    };
    window.addEventListener("innernet:organism-hold", onHold as EventListener);

    (async () => {
      // ── FUTURE ENGINE (default) — the next-gen single-pass SDF organism mounted
      // behind the exact `field` surface the rest of this component drives, so
      // every behaviour (focus dives, rotation, firing, age law, publish, veil,
      // pointer, buses) works unchanged. `engine="coil"` falls through to the
      // previous BloomField/OrganismGL pair.
      if (engine === "future") {
        const [{ FutureOrganism, futureTunersFromSeed, FUTURE_TUNER_DEFAULTS }, { vividColorState }] = await Promise.all([
          import("../lib/organism/future/futureOrganism"),
          import("../lib/organism/future/identity"),
        ]);
        const canvas = canvasRef.current;
        const glCanvas = glCanvasRef.current;
        if (disposed || !canvas || !glCanvas) return;

        // A coarse pointer or a phone-sized viewport counts too — see
        // lib/organism/deviceBudget. The old UA-only test meant a tablet reporting as
        // a Mac, or a phone in desktop-site mode, ran the desktop budget on phone
        // silicon.
        const lowPower = isLowPowerDevice();
        // ── THE MOBILE BUDGET (the 50–60fps decision) ──
        // A phone gets ONE GL context (the veil never mounts there — see the veil
        // effect), HALF the frame rate (30fps for a slow ambient bloom), and no
        // preserved drawing buffer. DPR backing store: 0.72 — up from 0.55. The
        // landing zooms netti large at the hero, where 0.55 read soft/cheap; the
        // GPU we freed (the veil no longer uploads a framebuffer, the per-frame
        // reflow is gone, mobile is 30fps-capped) buys the sharper backing store.
        // When netti is SMALL in later sections, 0.72 of a small element is still
        // cheap — so it "boosts at the hero, tones down" for free.
        // A MODEST GPU (integrated / software — see organismBudget) is the case
        // the device-class tests miss: many cores, a fine pointer, a big window,
        // and no discrete graphics. Fill rate is this renderer's entire cost, so
        // it gets the mobile treatment on PIXELS and FRAMES while keeping every
        // bit of colour and softness: 0.78 DPR, 40fps (still even on 120Hz).
        const modest = !lowPower && isModestGpu();
        // a CPU rasteriser gets a bloom the size of a stamp at a handful of
        // frames — enough to be the creature, not enough to hang the tab
        // (lib/organism/deviceBudget, isSoftwareGpu).
        const software = isSoftwareGpu();
        // ⚠️ 0.9, NOT 1.0, ON THE FULL TIER. This renderer is fill-rate bound —
        // cost is (backing pixels × fps) and nothing else. A capable desktop was
        // asking for a full 1× backing store of a full-screen bloom at 60fps and,
        // on an integrated-graphics MacBook, simply missing that cap; a missed
        // 60 reads as judder, which is why the 30fps phone felt SMOOTHER than the
        // desktop. Ten percent off each axis is ~19% fewer pixels per frame and
        // is invisible on a soft bloom with no hard edges — the look and the size
        // are untouched, the frame budget is not.
        const effDpr = software
          ? 0.28
          : maxDprProp != null
            ? Math.min(maxDprProp, lowPower ? 1.15 : 2)
            : Math.max(0.5, (lowPower ? 0.72 : modest ? 0.78 : 0.9) * Math.min(1, renderScale + 0.2));
        const fut = new FutureOrganism({
          canvas: glCanvas,
          maxDpr: effDpr,
          // fps ceiling: 30 on phones. On desktop, CAP AT 60 (was uncapped) — a slow
          // ambient bloom is visually identical at 60fps, but on a 120Hz/ProMotion
          // display "uncapped" meant rendering 120 frames/s (2× the GPU + compositor
          // work) and starving the page's own 120fps scroll. 60 divides both 60Hz
          // and 120Hz cleanly, so the organism's motion stays perfectly even.
          fpsCap: software ? 10 : lowPower ? 30 : modest ? 40 : 60,
          // Nothing samples the organism canvas anymore — the ascii veil fakes
          // netti's location with an analytic mask instead of reading its pixels —
          // so we never need to preserve the drawing buffer (tiled GPUs can skip
          // the per-frame full-framebuffer copy).
          preserveDrawingBuffer: false,
        });

        // ADAPTER — the `field` surface (only what this component actually calls)
        const adapter = {
          __future: fut,
          color: live.current.color ?? live.current.theme.organism,
          bg: live.current.bg ?? renderedGroundRGB() ?? live.current.theme.bgRgb,
          // `now` is the FIRST paint only — a mount has no colour to travel out
          // of, and easing up from the engine's default would flash a stranger's
          // palette across every navigation. Every later call morphs.
          setColor(c: ColorState, now = false) {
            adapter.color = c; // publish() reads the UNlifted pins (UI tint)
            fut.setColor(vividColorState(c), now); // the engine pools real pigment
          },
          setBackgroundRGB(rgb: RGB) {
            adapter.bg = rgb;
            fut.setBg(rgb);
          },
          setAge: (v: number) => fut.setAge(v),
          setRotation: (rad: number) => fut.setRotation(rad),
          setPointer: (x: number | null, y?: number) => fut.setPointer(x, y),
          setPaused: () => {}, // the engine's own loop gates on document.hidden
          // rest ladder — the calm bus feeds this (readers/modals frosting over)
          setCalm: (v: number) => fut.setCalm(v),
          setDefocus: (v: number) => fut.setDefocus(v),
          setPose: (v: number) => fut.setPose(v),
          setSeed: (s: number) => fut.setSeed(s),
          fireStream: () => fut.fire(),
          fireStreamBurst: (_n?: number) => fut.burst("out"),
          clearStreams: () => fut.clearPulses(),
          focusLeaf: (f: OrganismFocus) => fut.focusOn(f),
          clearFocus: () => fut.clearFocus(),
          // live tuner passthrough — the drive bus bumps venation/nodes to express
          // the developers section (clarity up, not opacity up). Single-pass engine,
          // so a tuner change takes effect next frame with no rebuild.
          setTuner: (k: string, v: number) =>
            fut.setTuner(k as Parameters<typeof fut.setTuner>[0], Math.min(1, Math.max(0, v))),
          // showcase morphs silhouettes through the formation lattice
          setForm: (v: number) => fut.setTuner("formation", Math.min(1, Math.max(0, v))),
          // THE WAY HOME. A section (or the overture's becoming) can morph netti
          // into anything; this is how it becomes ITSELF again — the mint-safe
          // dials derived from this person's seed, the body they actually own.
          //
          // Without it, whoever morphed last simply won: the overture ended on a
          // patch of `{}` (a no-op), so a brand-new account lived in the folded
          // shape of the birth's last frame — a huge cell and stubby petals —
          // for the entire life of the session. A morph must always be able to
          // say "and now back to you".
          restoreIdentity: () => {
            fut.setTuners({
              ...(seed != null ? futureTunersFromSeed(seed) : FUTURE_TUNER_DEFAULTS),
              ...(bodyRef.current ?? {}),
              revolve: 0, // rotation belongs to the choreography, never the mint
              ...(lowPower
                ? { motes: 0.22, venation: 0.24, nodes: 0.28, abundance: 0.44 }
                : modest
                  ? { motes: 0.3, venation: 0.34, nodes: 0.34 }
                  : {}),
            });
          },
          setMacro: (_k: MacroKey, _v: number) => {},
          resize: () => fut.resize(),
          destroy: () => fut.destroy(),
        };
        fieldRef.current = adapter;
        glRef.current = {
          ok: fut.ok,
          resize: () => fut.resize(),
          stop: () => fut.stop(),
          exportTransparentPng: () => fut.exportTransparentPng(2048),
        };

        // IDENTITY — a seeded mount is a PERSON: arrangement + mint-safe tuners
        // derive from their seed (deterministic, differentiated). Motion dials
        // stay product defaults; the mount's spin authority owns rotation.
        //
        // MOUNT SETS, IT DOES NOT MORPH (`now`) — this organism is not changing
        // into anything, it is arriving. Everything from here to `start()` is
        // the shape it is born wearing.
        if (seed != null) {
          fut.setSeed(seed);
          fut.setTuners(futureTunersFromSeed(seed), true);
        }
        if (bodyRef.current) fut.setTuners(bodyRef.current, true);
        fut.setTuners({ revolve: 0 }, true); // rotation belongs to the choreography
        // LOW-POWER SHADER TIER — trim the two hottest per-pixel loops (the 40-mote
        // loop + the venation ray-fan) and the petal count so the fragment shader
        // hits 60fps on phones. The soft bloom hides the reduction at arm's length.
        if (lowPower) {
          // the veil never mounts on phones, so the organism's OWN motes carry
          // the sparkle/dither feel (single pass, no second context) — kept at a
          // modest level; venation/nodes stay (they ARE the network read) and
          // abundance holds the petal richness.
          fut.setTuner("motes", 0.22, true);
          fut.setTuner("venation", 0.24, true);
          fut.setTuner("nodes", 0.28, true);
          fut.setTuner("abundance", 0.44, true);
        }
        // the surface's own dressing, applied while netti is still arriving so
        // the first painted frame already wears it (never a visible re-dress).
        if (tuneRef.current) {
          for (const k in tuneRef.current) {
            fut.setTuner(k as Parameters<typeof fut.setTuner>[0], Math.min(1, Math.max(0, tuneRef.current[k])), true);
          }
        }
        adapter.setColor(live.current.color ?? live.current.theme.organism, true);
        adapter.setBackgroundRGB(live.current.bg ?? renderedGroundRGB() ?? live.current.theme.bgRgb);
        fut.setAge(curAge, true);
        // a mount mid-frost (navigating straight into a reader) starts at rest
        fut.setCalm(currentOrganismCalm());
        // …and a mount mid-read starts out of focus, with nothing to ease from
        if (lensRef.current) {
          const now = currentNettiFocus();
          fut.setDefocus(now.focus, true);
          fut.setPose(now.read, true);
        }
        if (fut.ok) {
          fut.resize();
          fut.start();
          glCanvas.style.opacity = "1";
          canvas.style.opacity = "0";
        }
        if (focus) fut.focusOn(focus);
        fut.setRotation(rotation);

        if (apiRef) {
          apiRef.current = {
            exportPng: (px?: number) => (fut.ok ? fut.exportTransparentPng(px ?? 2048) : ""),
            exportPngs: (darks: number[], px?: number) => (fut.ok ? fut.exportForGrounds(darks, px ?? 2048) : darks.map(() => "")),
            setMacro: () => {},
            setAge: (v: number) => fut.setAge(v),
            setHue: (h: number) =>
              adapter.setColor(harmonize(h, "analogous", { sat: 0.62, light: 0.5, contrast: 0.6 })),
            setColor: (c: ColorState) => adapter.setColor(c),
            setBg: (rgb: RGB) => adapter.setBackgroundRGB(rgb),
            setRotation: (rad: number) => fut.setRotation(rad),
            reseed: () => fut.setSeed((Math.random() * 1e6) | 0),
            fire: () => fut.fire(),
          };
        }
      } else {
      const [{ BloomField }, { OrganismGL }] = await Promise.all([
        import("../lib/organism/bloomField"),
        import("../lib/organism/organismGL"),
      ]);
      const canvas = canvasRef.current;
      const glCanvas = glCanvasRef.current;
      if (disposed || !canvas || !glCanvas) return;

      // WARM COIL — a mount-level toggle applies the blob's dialed-in worm PRESET so the
      // product coil looks EXACTLY like the /blob worm·coil (slim pointed petals, right
      // density/bleed, glass, inner light) — not the mount's flower-tuned macros (which
      // rendered it boxy + washed). The preset WINS over the caller macros for the shape/
      // light keys; age + spread + gravity still ride in from the mount (memory-driven).
      // At warm=false nothing is injected → flower unchanged. Same minted palette (no re-roll).
      const effectiveMacros: Partial<MacroState> | undefined = warm
        ? {
            ...(macros ?? {}),
            warm: 1, form: 0.46, spiralTightness: 0.76, petalBulb: 0.14, petalCadence: 0.44,
            density: 0.42, roundness: 0.60, roughness: 0.40, bleed: 0.60, breath: 0.40,
            glass: 0.37, innerLight: 0.55, veinGlow: 0.65, veinDensity: 0.57, veinOpacity: 0.47,
            particles: 0.55, core: 0, gravity: 0.10,   // barely-there petal/cell give near the pointer (the core-light follow is the main reaction)
          }
        : macros;
      // LOW-POWER GOVERNOR — on mobile / few-core devices the organism renders at a
      // lower backing resolution (DPR 1, renderScale ×0.85). Fill-rate is the entire
      // cost of this renderer, so this alone keeps low-end devices at 60fps with no
      // visible loss (the organism is a soft watercolour — undersampling is free).
      const isMobile = isTouchScreenDevice();
      const lowPower = isLowPowerDevice();
      const modest2 = !lowPower && isModestGpu();
      const effDpr = isSoftwareGpu() ? 0.35 : lowPower ? 1.0 : modest2 ? 1.15 : 1.5;
      const effScale = renderScale * (lowPower ? 0.85 : modest2 ? 0.9 : 1);
      // WARM SIZE — the coil renders a touch smaller than natural (more on mobile) so
      // it sits comfortably inside its canvas: nothing ever presses against the canvas
      // bounds, so no edge can read as a container. Uniform (petals + reach + cell).
      const sizeFactor = warm ? (isMobile ? 0.8 : 0.9) : 1;
      const field = new BloomField({
        canvas,
        reservedRight: 0,
        renderScale: effScale,
        maxDpr: effDpr,
        sizeFactor,
        mode,
        ...(effectiveMacros ? { macros: effectiveMacros } : {}),
        ...(seed != null ? { seed } : {}),
      });
      fieldRef.current = field;

      field.setColor(live.current.color ?? live.current.theme.organism);
      field.setBackgroundRGB(live.current.bg ?? renderedGroundRGB() ?? live.current.theme.bgRgb);
      field.setAge(curAge);
      // The GPU re-skin (OrganismGL) drives the sim headlessly (field.tick) and
      // renders the watercolor organism on the GPU into its OWN webgl2 canvas,
      // stacked over the field's 2D canvas. A single <canvas> can only hold one
      // context type, so the GPU layer MUST be a separate element — sharing one
      // canvas makes getContext("webgl2") return null after the field grabs 2D,
      // which silently dropped us to the Canvas2D fallback (the bug). If WebGL2
      // is unavailable we run the field's own Canvas2D loop so it always shows.
      const gl = new OrganismGL(glCanvas, field, { reservedRight: 0, maxDpr: effDpr });
      if (gl.ok) {
        glRef.current = gl;
        field.resize(); // size the field canvas so currentScale() feeds the snapshot
        gl.resize();
        gl.start();
        glCanvas.style.opacity = "1";
        canvas.style.opacity = "0"; // the 2D layer stays blank beneath the GPU one
      } else {
        glCanvas.style.opacity = "0";
        field.start();
        field.resize();
      }
      if (focus) field.focusLeaf(focus);
      field.setRotation(rotation);

      // TOOL API (/netti) — the imperative handle a tool surface drives. Same canonical
      // component + engine as every other mount; the tool just gets levers + export.
      if (apiRef) {
        apiRef.current = {
          exportPng: () => (glRef.current?.ok ? glRef.current.exportTransparentPng() : ""),
          // the legacy engine has one look — every ground gets the same frame
          exportPngs: (darks: number[]) => {
            const one = glRef.current?.ok ? glRef.current.exportTransparentPng() : "";
            return darks.map(() => one);
          },
          setMacro: (k: MacroKey, v: number) => fieldRef.current?.setMacro(k, v),
          setAge: (v: number) => fieldRef.current?.setAge(v),
          setHue: (h: number) => fieldRef.current?.setColor(harmonize(h, "analogous", { sat: 0.62, light: 0.5, contrast: 0.6 })),
          setColor: (c: ColorState) => fieldRef.current?.setColor(c),
          setBg: (rgb: RGB) => fieldRef.current?.setBackgroundRGB(rgb),
          setRotation: (rad: number) => fieldRef.current?.setRotation(rad),
          reseed: () => fieldRef.current?.setSeed((Math.random() * 1e6) | 0),
          fire: () => fieldRef.current?.fireStream?.(),
        };
      }
      } // ← end coil branch; everything below drives whichever engine mounted

      const canvas = canvasRef.current!;
      const glCanvas = glCanvasRef.current!;
      const field = fieldRef.current;
      if (disposed || !field) return;

      // BOOT SIGNAL — the engine is mounted and rendering (either generation,
      // GL or fallback). A boot veil (the landing preloader) listens for this to
      // hand the screen over from the growing-netti placeholder to the live one.
      try {
        window.dispatchEvent(new Event("innernet:organism-ready"));
      } catch {
        /* never let a listener error break the mount */
      }

      // Observe the WRAPPER, not a canvas: field.resize() pins an explicit px
      // width on the field canvas, which would freeze a canvas-targeted observer
      // after the first fire. The wrapper stays width/height:100%, so it always
      // tracks layout and re-drives both resizes (field → currentScale, gl → buffer).
      ro = new ResizeObserver(() => {
        field.resize();
        glRef.current?.resize();
      });
      ro.observe(wrapRef.current ?? canvas);
      document.addEventListener("visibilitychange", onVis);

      // POINTER-FOLLOW — the backdrop LISTENS to the cursor globally (its own wrapper is
      // pointer-events:none so it never intercepts clicks / clashes with the UI above).
      // We map the window pointer into the GL canvas's on-screen rect (which already bakes
      // in the anchor/offset/scale transform) → field.setPointer → the cell's core light
      // tracks the cursor + petals give subtly near it (gravity). Reduced-motion: skip.
      // touch devices have no hovering cursor — the follow work (a layout read
      // per move) is pure waste there, so it only wires up where hover exists.
      const canHover = window.matchMedia?.("(hover: hover)").matches ?? true;
      if (!reduce && canHover) {
        onPointer = (e: PointerEvent) => {
          const f = fieldRef.current;
          if (!f) return;
          const r = glCanvas.getBoundingClientRect();
          if (r.width < 1 || r.height < 1) return;
          const cw = glCanvas.clientWidth || r.width;
          const chh = glCanvas.clientHeight || r.height;
          f.setPointer(((e.clientX - r.left) / r.width) * cw, ((e.clientY - r.top) / r.height) * chh);
        };
        onPointerLeaveWin = () => fieldRef.current?.setPointer(null);
        window.addEventListener("pointermove", onPointer, { passive: true });
        window.addEventListener("pointerleave", onPointerLeaveWin, { passive: true });
      }

      // publish the live palette so the whole UI carries the organism's color.
      //
      // These four are REGISTERED, INHERITED custom properties on :root, which makes
      // each write a document-wide style invalidation (and a repaint of every surface
      // tinted by them). Publishing an unchanged palette therefore costs a great deal
      // and buys nothing, so the tint is QUANTIZED to a step no eye can follow and
      // written only when that step moves. Phones get a coarser step: the retint
      // transition is off there (see globals.css), so the steps ARE the motion.
      const cheapTint = isLowPowerDevice();
      const hueStep = cheapTint ? 3 : 1;
      const satStep = cheapTint ? 0.04 : 0.02;
      let lastSig = "";
      // ON A PHONE, NEVER WHILE THE PAGE IS MOVING. Even unanimated, one publish is a
      // document-wide recalc and a repaint of every tinted surface — measured at tens
      // of milliseconds on a big page. Spending that during a scroll is spending it at
      // the exact moment frames matter most, so the tint holds while you move and
      // catches up a beat after you stop (the interval below is already re-running).
      // The accents this feeds are small marks; the delay reads as nothing.
      let lastScrollAt = 0;
      const onScrollTint = () => { lastScrollAt = performance.now(); };
      // capture on the document: a scroll event does not bubble, and on a stage
      // page the content scrolls in an element, never the window — only a capture
      // listener hears that (and still hears the window's own scroll, elsewhere)
      if (cheapTint) document.addEventListener("scroll", onScrollTint, { passive: true, capture: true });
      publishScrollCleanup = () => {
        if (cheapTint) document.removeEventListener("scroll", onScrollTint, { capture: true });
      };
      const publish = () => {
        const f = fieldRef.current;
        if (!f?.color) return;
        if (cheapTint && performance.now() - lastScrollAt < 240) return;
        const c = f.color;
        const q = (v: number, step: number) => Math.round(v / step) * step;
        const sig = [
          Math.round(q(c.primary.hue, hueStep)),
          q(c.primary.sat, satStep).toFixed(2),
          q(c.primary.light, satStep).toFixed(2),
          q(c.contrast ?? 0, satStep).toFixed(2),
        ].join("|");
        if (sig === lastSig) return;
        lastSig = sig;
        const root = document.documentElement.style;
        root.setProperty("--organism-hue", String(Math.round(c.primary.hue)));
        try {
          const pal = derivePalette(c);
          root.setProperty("--organism-color", rgbToCss(pal.body));
          root.setProperty("--organism-core", rgbToCss(pal.core));
          root.setProperty("--organism-fringe", rgbToCss(pal.fringe));
        } catch {
          /* palette derive is pure; ignore the impossible */
        }
      };

      if (doPublish()) {
        // enable the smooth retint transition only after we're live, so the
        // first apply (and SSR → hydration) lands instantly.
        document.documentElement.classList.add("organism-live");
        publish();
      }

      // ALWAYS installed; every tick reads the live flags, so a mount can turn
      // its showcase/wander/publish on or off after the fact (160ms of nothing
      // when all three are off — cheaper than a stale hue cycling forever).
      {
        const t0 = performance.now();
        const STOP_MS = 9000; // ~9s per hue stop (wander)
        let formIdx = 0;
        let lastFormAt = performance.now();
        driveTimer = setInterval(() => {
          if (document.hidden) return;
          const driving = doShowcase() || doWander();
          if (!driving && wasDriving) {
            // the drive let go — hand the creature back its own colour.
            const own = colorPropRef.current;
            if (own) field.setColor(own);
          }
          wasDriving = driving;
          // …and when PUBLISHING stops (the walk graduated), the UI's tint goes
          // back to the theme's own organism at once — otherwise the last stage
          // hue stayed painted on :root and the dashboard read subtly off-tint
          // (a title here, a chip there) until the next full load.
          const publishing = doPublish();
          if (!publishing && wasPublishing) {
            try {
              document.documentElement.classList.remove("organism-live");
              const t = live.current.theme;
              const vars = computeThemeVars(t.bgRgb, t.organism);
              const root = document.documentElement.style;
              for (const k in vars) root.setProperty(k, (vars as Record<string, string>)[k]);
              lastSig = "";
            } catch { /* theme vars are best-effort */ }
          }
          wasPublishing = publishing;
          if (doShowcase()) {
            // COLOUR — one smooth hue drift, kept NEAR-MONOCHROMATIC (a tight ±16°
            // spread + low contrast) so the bloom reads as ONE hue: the same hue
            // the ink / buttons / text wear. publish() below sends primary.hue to
            // --organism-*, so the organism and the UI move in unison (no "random"
            // mismatch between a multi-hue bloom and a single-hue text).
            const t = (performance.now() - t0) / 1000;
            const hue = (SHOWCASE_BASE_HUE + (t / SHOWCASE_PERIOD_S) * 360) % 360;
            field.setColor({
              primary: { hue, sat: 0.62, light: 0.5 },
              secondary: { hue: (hue + 16) % 360, sat: 0.58, light: 0.47 },
              tinge: { hue: (hue + 344) % 360, sat: 0.55, light: 0.54 },
              contrast: 0.2,
            });
            // GROUND — track the page's cycling background each tick so the bloom
            // composites seamlessly over it (uBg = the live ground, no rim halo).
            // The shader glows emissively as the ground darkens — "lit from within".
            const g = renderedGroundRGB();
            if (g) field.setBackgroundRGB(g);
            // SHAPE — step to a new silhouette only every ~7.6s (> the engine's
            // 1.1s morph crossfade), so each morph blends in cleanly, no glitch.
            const now = performance.now();
            if (now - lastFormAt >= SHOWCASE_FORM_MS) {
              lastFormAt = now;
              formIdx = (formIdx + 1) % SHOWCASE_FORMS.length;
              field.setForm?.(SHOWCASE_FORMS[formIdx]);
            }
          } else if (doWander()) {
            const e = (performance.now() - t0) / STOP_MS;
            const i = Math.floor(e) % HUE_JOURNEY.length;
            const j = (i + 1) % HUE_JOURNEY.length;
            const hue = lerpHue(HUE_JOURNEY[i], HUE_JOURNEY[j], smooth(e - Math.floor(e)));
            field.setColor(harmonize(hue, "triadic", { sat: 0.6, light: 0.52, contrast: 0.66 }));
          }
          if (doPublish()) publish();
        }, 160);
      }

      const doSpin = spin && !reduce;
      if (grow || doSpin) {
        // LIFE LOOP — a single requestAnimationFrame driving breath + ambient spin
        // + scrub-spin, every term dt-scaled so the motion is identical at any
        // refresh rate and immune to setInterval drift (the old jitter source).
        // Each frame, in order:
        //   target     = busAgeRef ?? agePropRef      (scrub override, else natural)
        //   displayAge → eases toward target          (absorbs any jump → no step)
        //   renderAge  = displayAge + breath + pulse  (clamped; never > natural+ε)
        //   scrubSpin  ∝ |d(displayAge)/dt|           (turns WHILE actively aging)
        const t0 = performance.now();
        let prevT = t0;
        const TAU_AGE = 0.14; // s — age ease time-constant: responsive yet smooth
        const BREATH_AMP = reduce ? 0 : 0.014; // tiny swell — holds a consistent size
        const BREATH_W = 0.5; // rad/s — a slow, barely-there breath
        const AMBIENT_RAD_PER_S = 0.09; // ~5°/s idle drift — grace over speed
        const SCRUB_COUPLE = 0.5; // |age/s| → rad/s of scrub spin
        const SCRUB_VEL_MAX = 0.9; // rad/s ceiling — a fling can never whip it
        const TAU_SCRUB = 0.12; // s — how fast scrub spin tracks then settles
        const frame = () => {
          rafId = requestAnimationFrame(frame);
          if (document.hidden) return;
          const now = performance.now();
          // clamp dt so a stall (tab throttle, GC pause) can't inject a jump.
          const dt = Math.min(0.05, Math.max(0, (now - prevT) / 1000));
          prevT = now;
          const t = (now - t0) / 1000;

          // ease the rendered base toward the live target (natural, or a scrub).
          const target = busAgeRef.current ?? agePropRef.current;
          displayAge.current += (target - displayAge.current) * (1 - Math.exp(-dt / TAU_AGE));
          // a memory-write swell decays back to natural, frame-rate independent.
          pulseAge.current *= Math.exp(-dt / 0.5);

          if (grow) {
            const breath = Math.sin(t * BREATH_W) * BREATH_AMP;
            // THE LAW: the present never reads bigger than natural (+ the tiny
            // breath/pulse). displayAge is already ≤ natural while scrubbing.
            // the floor is the natural-size law (a seated bloom never collapses to
            // nothing) — but a SCRUB on the bus is an explicit instruction, and the
            // birth page needs to take the creature all the way to a lone cell.
            const floor = busAgeRef.current != null ? 0 : 0.12;
            const renderAge = Math.min(0.92, Math.max(floor, displayAge.current + breath + pulseAge.current));
            field.setAge(renderAge);
          }

          // scrub spin — proportional to how fast the age is moving RIGHT NOW, so
          // the bloom turns as you scroll the timeline and settles when you stop.
          // Single source (the old code double-counted this in the loop AND the age
          // effect, which made the spin erratic).
          if (!reduce) {
            const ageVel = Math.abs(displayAge.current - prevDisplayAge.current) / Math.max(dt, 1e-3);
            const tgt = Math.min(SCRUB_VEL_MAX, ageVel * SCRUB_COUPLE);
            scrubSpinVel.current += (tgt - scrubSpinVel.current) * (1 - Math.exp(-dt / TAU_SCRUB));
          }
          prevDisplayAge.current = displayAge.current;

          if (doSpin) rotAmb.current += AMBIENT_RAD_PER_S * dt;
          if (scrubSpinVel.current > 1e-4) rotAmb.current += scrubSpinVel.current * dt;
          // the ambient drift never stops, so fold it: the engine takes the
          // shortest path to any rotation it is handed and 4π ≡ 0 on that path,
          // so this is invisible — and the number it receives stays small.
          if (rotAmb.current > 4 * Math.PI) rotAmb.current -= 4 * Math.PI;
          if (doSpin || grow || scrubSpinVel.current > 1e-4) {
            field.setRotation(rotBase.current + rotAmb.current);
          }
        };
        rafId = requestAnimationFrame(frame);
      }
    })().catch((err) => {
      // A failed chunk or a WebGL context that would not start used to be an
      // unhandled rejection: netti silently never mounted, and now that
      // ChunkRecovery listens for those it could have reloaded the page over a
      // decoration. The canvases are transparent, so nothing draws and the still
      // ground stays; the trip is reported so it is seen.
      reportClientError(err, "organism:mount");
    });

    return () => {
      disposed = true;
      ro?.disconnect();
      publishScrollCleanup?.();
      document.removeEventListener("visibilitychange", onVis);
      if (onPointer) window.removeEventListener("pointermove", onPointer);
      if (onPointerLeaveWin) window.removeEventListener("pointerleave", onPointerLeaveWin);
      if (driveTimer) clearInterval(driveTimer);
      window.removeEventListener("innernet:organism-hold", onHold as EventListener);
      if (rafId) cancelAnimationFrame(rafId);
      glRef.current?.stop?.();
      glRef.current = null;
      fieldRef.current?.destroy?.();
      fieldRef.current = null;
      if (apiRef) apiRef.current = null;
      // restore the theme's organism palette so a wandered/showcase hue doesn't
      // bleed into the next route on client navigation. Drop the live transition
      // so the reset snaps instantly rather than sweeping back through hues.
      if (doWander() || doShowcase()) {
        document.documentElement.classList.remove("organism-live");
        try {
          const t = live.current.theme;
          const vars = computeThemeVars(t.bgRgb, t.organism);
          const root = document.documentElement.style;
          for (const k of ["--organism-hue", "--organism-color", "--organism-core", "--organism-fringe"]) {
            if (vars[k] != null) root.setProperty(k, vars[k]);
          }
        } catch {
          /* ignore */
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // keep ground synced to the theme (blend + glow-on-dark) — preferring the
  // rendered body color so canvas and page can never split tone
  useEffect(() => {
    fieldRef.current?.setBackgroundRGB?.(bg ?? renderedGroundRGB() ?? theme.bgRgb);
  }, [bg, theme.bgRgb]);

  // COLOR PROP → the living palette. The engine is seeded with `color` at mount
  // (through live.current), but nothing pushed a LATER change, so a surface that
  // repaints per section — the deck, whose every space carries its own netti —
  // kept the boot palette forever (John, 2026-08-31). Pushing on change lets the
  // creature travel with the ground; passing a stable object means a surface
  // that never repaints never fires this.
  useEffect(() => {
    if (!color) return;
    fieldRef.current?.setColor?.(color);
  }, [color]);

  // focus / section change → glide+zoom+dim to the region (or clear)
  useEffect(() => {
    const f = fieldRef.current;
    if (!f) return;
    if (focus) f.focusLeaf(focus);
    else f.clearFocus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focus?.angle, focus?.dist, focus?.zoom, focus?.radius]);

  // rotation → spin the whole organism (eased), composed with ambient drift so
  // section/dive spins layer on top of the idle life without fighting it.
  useEffect(() => {
    rotBase.current = rotation;
    fieldRef.current?.setRotation?.(rotation + rotAmb.current);
  }, [rotation]);

  // TUNER PATCH changed → re-dress the ONE organism. Walking home first
  // (`restoreIdentity`) is what makes a patch reversible: without it whoever
  // dressed netti last simply wins, and a view that sets nothing inherits the
  // previous view's body. Keyed on the SERIALISED patch, because a caller's
  // object literal is a fresh reference on every render.
  const tuneKey = tune ? JSON.stringify(tune) : "";
  const tuneWas = useRef("");
  useEffect(() => {
    if (tuneKey === tuneWas.current) return;
    const had = tuneWas.current;
    tuneWas.current = tuneKey;
    const f = fieldRef.current;
    if (!f) return; // the boot closure applies the patch itself
    if (had) f.restoreIdentity?.();
    if (!tune) return;
    for (const k in tune) f.setTuner?.(k, tune[k]);
  }, [tuneKey, tune]);

  // the person's own body arrived (or changed) after mount — it is fetched, so
  // it usually lands a beat after the first frame. Identity re-settles (a morph,
  // not a cut) and the surface's dressing goes back on top.
  const bodyKey = body ? JSON.stringify(body) : "";
  const bodyWas = useRef(bodyKey);
  useEffect(() => {
    if (bodyKey === bodyWas.current) return;
    bodyWas.current = bodyKey;
    const f = fieldRef.current;
    if (!f) return;
    f.restoreIdentity?.();
    const t = tuneRef.current;
    if (t) for (const k in t) f.setTuner?.(k, t[k]);
  }, [bodyKey]);

  // `age` prop changed → it's the new NATURAL size. The life loop eases displayAge
  // toward it and the scrub-spin picks up the resulting age velocity on its own,
  // so there's nothing to nudge here — just hand the loop its new target.
  useEffect(() => {
    agePropRef.current = age;
  }, [age]);

  // IMPERATIVE AGE BUS — a timeline (activity log / commit history) scrubs the
  // bloom's age through a window event instead of React state, so dragging the
  // scroll re-ages the organism at 60fps without ever re-rendering the shell.
  // detail.age = number → scrub override; null → release back to natural size.
  useEffect(() => {
    if (!ageBus) return;
    const onAge = (e: Event) => {
      const d = (e as CustomEvent).detail;
      if (busKey ? d?.bus !== busKey : d?.bus) return; // not for this mount
      const v = d?.age;
      busAgeRef.current = typeof v === "number" ? v : null;
    };
    window.addEventListener("innernet:organism-age", onAge as EventListener);
    return () => window.removeEventListener("innernet:organism-age", onAge as EventListener);
  }, [ageBus, busKey]);

  // IMPERATIVE DRIVE BUS — a scroll controller repaints colour / ground / rotation
  // at 60fps through a window event instead of React props, so a landing can scrub
  // the bloom's whole mood as you scroll without ever re-rendering this shell.
  // detail: { color?, bg?, rotation?, veins?: 0..1 clarity, focus?: {angle,dist,zoom}
  //   | null to clear, fire?: number|true to fire neuron streams (rhythmic beats). }
  useEffect(() => {
    if (!driveBus) return;
    const onDrive = (e: Event) => {
      const d = (e as CustomEvent).detail || {};
      if (busKey ? d.bus !== busKey : d.bus) return; // not for this mount
      const f = fieldRef.current;
      if (!f) return;
      if (d.color) f.setColor?.(d.color);
      if (d.bg) f.setBackgroundRGB?.(d.bg);
      if (typeof d.rotation === "number") {
        rotBase.current = d.rotation;
        f.setRotation?.(d.rotation + rotAmb.current);
      }
      // developers section — venation clarity + node cells rise (density/clarity,
      // never opacity), and the membrane goes GLASS with a bioluminescent inner
      // light, so the organism reads technical and lit from within.
      if (typeof d.veins === "number") {
        f.setTuner?.("venation", d.veins);
        f.setTuner?.("nodes", d.veins * 0.9);
      }
      if (typeof d.glass === "number") {
        f.setTuner?.("glass", d.glass);
        f.setTuner?.("innerLight", Math.min(1, 0.4 + d.glass * 0.6));
      }
      // grow — the central cell shrinks to a seed and grows back
      if (typeof d.cell === "number") f.setTuner?.("cellSize", d.cell);
      // PERSONALITY — a section can morph netti's WHOLE form (petal shape, formation,
      // hue spread, motes…) by sending a tuner patch. Each section wears a different
      // body/character; the single-pass engine takes each next frame, no rebuild.
      if (d.tuners) {
        for (const k in d.tuners) {
          const v = d.tuners[k];
          if (typeof v === "number") f.setTuner?.(k as Parameters<NonNullable<typeof f.setTuner>>[0], v);
        }
      }
      // …and back to itself. `identity: true` hands every dial back to this
      // person's own mint — the counterpart to any patch above, so nothing that
      // morphs netti for a moment can keep it.
      if (d.identity) f.restoreIdentity?.();
      // branch section — pan through focus points; null resets to the whole body.
      if (d.focus !== undefined) {
        if (d.focus) f.focusLeaf?.(d.focus);
        else f.clearFocus?.();
      }
      // neuron firing — "soft" fires a single stream with NO body swell (the
      // shimmering presence); a number fires the full reaction scaled by it.
      if (d.fire === "soft") f.fireStream?.();
      else if (d.fire) fire.current(typeof d.fire === "number" ? d.fire : 1);
    };
    window.addEventListener("innernet:organism-drive", onDrive as EventListener);
    return () => window.removeEventListener("innernet:organism-drive", onDrive as EventListener);
  }, [driveBus, busKey]);

  // REST — dynamic rendering, baked in. Any reader/modal frost publishes a calm
  // level on the window bus (lib/organism/calmBus); the engine winds fps +
  // backing resolution + its hottest shader loops down with it and back up the
  // moment the frost lifts. Always listening — surfaces only declare their
  // frost, no per-mount wiring. (This is what un-lags the reader views: the
  // scrim's full-viewport backdrop-blur re-runs only when the organism draws.)
  useEffect(() => {
    const onCalm = (e: Event) => {
      const lv = (e as CustomEvent).detail?.level;
      if (typeof lv === "number") fieldRef.current?.setCalm?.(Math.min(1, Math.max(0, lv)));
    };
    window.addEventListener(CALM_EVENT, onCalm as EventListener);
    return () => window.removeEventListener(CALM_EVENT, onCalm as EventListener);
  }, []);

  // THE LENS — the page's focus pull (lib/os/nettiFocus) eases this netti out
  // of focus inside the engine, where it costs least; a netti without the lens
  // is held sharp (it is the hero where it stands).
  useEffect(() => {
    if (!lens) {
      fieldRef.current?.setDefocus?.(0);
      fieldRef.current?.setPose?.(0);
      return;
    }
    const onFocus = (e: Event) => {
      const d = (e as CustomEvent<{ focus: number; read: number }>).detail;
      if (typeof d?.focus === "number") fieldRef.current?.setDefocus?.(Math.min(1, Math.max(0, d.focus)));
      if (typeof d?.read === "number") fieldRef.current?.setPose?.(d.read);
    };
    const now = currentNettiFocus();
    fieldRef.current?.setDefocus?.(now.focus);
    fieldRef.current?.setPose?.(now.read);
    window.addEventListener(FOCUS_EVENT, onFocus as EventListener);
    return () => window.removeEventListener(FOCUS_EVENT, onFocus as EventListener);
  }, [lens]);

  // ASCII VEIL — a third overlay canvas that samples the live GL organism and
  // renders a shimmering ASCII glyph field that dissolves into the ground. Client-
  // only (WebGL2), so it's built in an effect on mount; the canvas SSRs empty.
  // The GL canvas already carries `preserveDrawingBuffer`, so the overlay can
  // re-sample the organism's rendered pixels each frame. Presence-masked in-shader
  // → it renders only ON the organism, never full-screen.
  useEffect(() => {
    if (!veil) return;
    // ── THE MOBILE DECISION: no second GL context on phones, ever. ──
    // The veil re-uploads the organism's full frame as a texture every draw —
    // on mobile GPUs that (plus two live contexts) is what turned scrolling
    // into a slideshow and got the page's contexts evicted ("the crash"). At
    // phone DPR the glyph dither also degraded into mush. The organism's own
    // in-shader motes carry the sparkle on phones; the veil stays a desktop
    // material. This must mirror `veilActive` in the mount effect (which frees
    // preserveDrawingBuffer on phones because nothing samples the canvas).
    // Touch-screen form factors only — deliberately NOT the wider low-power test: a
    // narrow window on a desktop is still a desktop, and the veil is a material the
    // design depends on there.
    if (isTouchScreenDevice()) return;
    let disposed = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let overlay: any = null;
    let ro: ResizeObserver | null = null;
    // DYNAMIC DIALER — a section can change the veil's presence/characters LIVE
    // (window `innernet:veil-tune`, detail = a params patch, e.g. {sparse, preset,
    // opacity, organism}). Eased-free: it just merges into the running params, so
    // dev pages can bump the dither up + swap glyphs, most places stay tasteful.
    let onTune: ((e: Event) => void) | null = null;
    let onVeilCalm: ((e: Event) => void) | null = null;
    let bootTimer: ReturnType<typeof setTimeout> | null = null;
    (async () => {
      const { PostOverlay, asciiConfig } = await import("../lib/organism/shaders");
      // BOOT ORDER — the veil is a *second* GL context sampling the first. Let
      // the organism itself claim the GPU for its first frames (and the landing
      // preloader hand off) before this context compiles + starts uploading.
      await new Promise<void>((res) => {
        bootTimer = setTimeout(res, 900);
      });
      const overlayCanvas = overlayCanvasRef.current;
      const glCanvas = glCanvasRef.current;
      if (disposed || !overlayCanvas || !glCanvas) return;
      // PERF: the veil runs its own WebGL context and re-uploads the organism frame
      // each draw (its dominant cost) — cap it hard (DPR 1, and a lower cadence on
      // mobile). A soft shimmering dither is visually identical at ~22fps.
      const cfg = asciiConfig();
      if (veilTune) Object.assign(cfg.params, veilTune);
      const veilMobile = isLowPowerDevice();
      // the SECOND context is the most expensive thing on the page for a machine
      // with integrated graphics — it re-uploads netti's frame every draw. On a
      // modest GPU it renders at half resolution and a third of the cadence
      // (still a shimmer, a third of the fill), rather than not at all: the frost
      // IS part of the look, so it is toned down, never dropped.
      const veilModest = !veilMobile && isModestGpu();
      // the veil is a SECOND GL context — keep it cheap: reduced-res on mobile
      // (the soft dither hides it), and a low cadence (a shimmer reads
      // identically slow). Desktop runs near-native DPR so the glyphs read crisp,
      // not blurry (the dither must look sharp, esp. in the film + at 2K).
      overlay = new PostOverlay(overlayCanvas, glCanvas, cfg, {
        reservedRight: 0,
        maxDpr: veilMobile ? 0.6 : veilModest ? 0.7 : 1.4,
        fps: veilMobile ? 18 : veilModest ? 12 : 30,
      });
      // track the live ground the field composites over, so the presence mask
      // (distance from ground) stays correct as the ground cycles.
      overlay.setBgSource(() => fieldRef.current?.bg ?? live.current.theme.bgRgb);
      overlay.start();
      ro = new ResizeObserver(() => overlay?.resize?.());
      ro.observe(wrapRef.current ?? overlayCanvas);
      onTune = (e: Event) => {
        const patch = (e as CustomEvent).detail as Record<string, number> | undefined;
        if (patch && overlay?.params) Object.assign(overlay.params, patch);
      };
      window.addEventListener("innernet:veil-tune", onTune as EventListener);
      // the veil rests with the organism — under a reader frost its per-draw
      // source re-upload (its dominant cost) drops to the same slow heartbeat.
      onVeilCalm = (e: Event) => {
        const lv = (e as CustomEvent).detail?.level;
        if (typeof lv === "number") overlay?.setCalm?.(lv);
      };
      window.addEventListener(CALM_EVENT, onVeilCalm as EventListener);
      overlay.setCalm?.(currentOrganismCalm());
    })().catch((err) => {
      // the veil is a second, optional layer: if it cannot start, netti is the
      // same netti without its frost, and the page carries on
      reportClientError(err, "organism:veil");
    });
    return () => {
      disposed = true;
      if (bootTimer) clearTimeout(bootTimer);
      ro?.disconnect();
      if (onTune) window.removeEventListener("innernet:veil-tune", onTune as EventListener);
      if (onVeilCalm) window.removeEventListener(CALM_EVENT, onVeilCalm as EventListener);
      overlay?.stop?.();
      overlay = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [veil]);

  // EXPLICIT pulse prop bumped → fire the neurons (a stream travelling the
  // lattice + a body swell/spin the life loop settles back). Driven by a parent
  // (e.g. OsShell.firePulse on a record/refine action).
  useEffect(() => {
    if (!pulse) return;
    fire.current(1);
  }, [pulse]);

  // LIVE memory feed fired → same firing path. Each flurry (debounced burst of
  // in-app `innernet:memory` events + Supabase realtime INSERTs on the user's
  // feed) bumps liveMem.pulse; we fire proportionally to how many events folded
  // into it (liveMem.burst), so a bulk write reads as a bigger flurry. This is
  // why the organism lights up on ANY memory change with zero call-site wiring.
  useEffect(() => {
    if (!liveMem.pulse) return;
    fire.current(liveMem.burst || 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveMem.pulse]);

  return (
    <div
      ref={wrapRef}
      className={className}
      style={{ position: "relative", width: "100%", height: "100%", ...style }}
    >
      {/* ph-no-capture on every canvas: session replay must never try to
          record the live organism (lib/analytics/replayMask, COST_SURFACES). */}
      <canvas
        ref={canvasRef}
        aria-hidden
        className="ph-no-capture"
        style={{ position: "absolute", inset: 0, display: "block", width: "100%", height: "100%" }}
      />
      <canvas
        ref={glCanvasRef}
        aria-hidden
        className="ph-no-capture"
        style={{ position: "absolute", inset: 0, display: "block", width: "100%", height: "100%" }}
      />
      {veil && (
        <canvas
          ref={overlayCanvasRef}
          aria-hidden
          className="ph-no-capture ob-veil"
          style={{
            position: "absolute",
            inset: 0,
            display: "block",
            width: "100%",
            height: "100%",
            zIndex: 1,
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
}
